import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";

// Kunci LocalStorage yang dipakai wrapper Capacitor / WebView untuk
// menyuntikkan token FCM setelah `PushNotifications.register()`.
const LS_KEY = "fcm_token";
const STATUS_KEY = "fcm_token:status";
// Menandai token yang sudah kita sinkron ke `profiles.fcm_token` untuk
// user tertentu agar tidak melakukan UPDATE berulang setiap render.
const SYNCED_KEY = "fcm_token:synced";
const REGISTRATION_FLAG_KEY = "fcm_token:registration_requested";
const SYNC_RETRY_DELAYS = [500, 1500, 3000, 7000, 15000];
let nativeRegistrationStarted = false;
let foregroundListenerStarted = false;

type NotificationData = Record<string, unknown> | undefined;

function buildNotificationUrl(data: NotificationData): string | null {
  if (!data) return null;
  const rawUrl = typeof data.url === "string" ? data.url : null;
  const roomId = typeof data.chat_room_id === "string"
    ? data.chat_room_id
    : typeof data.room_id === "string"
      ? data.room_id
      : null;
  if (rawUrl === "/chat" || rawUrl?.startsWith("/chat?")) {
    return roomId ? `/chat?room=${encodeURIComponent(roomId)}` : "/chat";
  }
  return rawUrl?.startsWith("/") ? rawUrl : null;
}

function rememberPendingNotificationUrl(url: string) {
  try { window.sessionStorage.setItem("notification:pendingUrl", url); } catch { /* ignore */ }
}

function isCallData(data: NotificationData): boolean {
  if (!data) return false;
  const t = (data as Record<string, unknown>).type;
  return typeof t === "string" && t === "call";
}

function getNotificationChannelId(_data: NotificationData): "default" {
  // Channel khusus yang pernah dibuat dengan importance rendah tidak bisa
  // dinaikkan oleh update web. Pakai channel default yang sudah terbukti muncul
  // di Android 12 ke bawah untuk pengumuman.
  return "default";
}

function getNotificationTitle(notification: { title?: string | null; data?: NotificationData }): string {
  const data = notification.data as Record<string, unknown> | undefined;
  return notification.title ??
    (typeof data?.notification_title === "string" ? data.notification_title : undefined) ??
    (typeof data?.title === "string" ? data.title : undefined) ??
    "Pesan baru";
}

function getNotificationBody(notification: { body?: string | null; data?: NotificationData }): string {
  const data = notification.data as Record<string, unknown> | undefined;
  return notification.body ??
    (typeof data?.notification_body === "string" ? data.notification_body : undefined) ??
    (typeof data?.body === "string" ? data.body : undefined) ??
    "Ada notifikasi baru masuk";
}

async function ensureNativeNotificationChannels() {
  const [{ PushNotifications }, { LocalNotifications }] = await Promise.all([
    import("@capacitor/push-notifications"),
    import("@capacitor/local-notifications"),
  ]);

  const channels = [
    {
      id: "default",
      name: "Notifikasi",
      description: "Notifikasi MI Al-Munajah",
      importance: 4,
      visibility: 1,
      vibration: true,
    },
    {
      id: "tagihan",
      name: "Tagihan Siswa",
      description: "Notifikasi tagihan dan pembayaran siswa",
      importance: 5,
      visibility: 1,
      vibration: true,
    },
  ] as const;

  for (const channel of channels) {
    try { await PushNotifications.createChannel(channel); } catch { /* Android only / already exists */ }
    try { await LocalNotifications.createChannel(channel); } catch { /* Android only / already exists */ }
  }
}

function queueNotificationOpen(data: NotificationData) {
  // Panggilan masuk/keluar ditangani sepenuhnya oleh CallCenter + notifikasi
  // native (statusbar ID 2026). Jangan pernah auto-navigate ke /chat hanya
  // karena push panggilan diterima/di-tap — itu memicu "reload mendadak"
  // ke menu chat saat user sedang di halaman lain.
  if (isCallData(data)) return;
  const url = buildNotificationUrl(data);
  if (!url) return;
  rememberPendingNotificationUrl(url);
  window.dispatchEvent(new CustomEvent("notification-open-url", { detail: { url } }));
}

function rememberStatus(status: string) {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STATUS_KEY, status);
    }
  } catch { /* ignore */ }
}

declare global {
  interface Window {
    onFCMTokenReceived?: (token: string) => void;
    injectFCMToken?: (token: string) => void;
    setFCMToken?: (token: string) => void;
    FCMToken?: string;
    fcmToken?: string;
    AndroidBridge?: { getFCMToken?: () => string | null };
    Android?: { getFCMToken?: () => string | null };
  }
}

function readNativeInjectedToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    const candidates: unknown[] = [
      window.FCMToken,
      window.fcmToken,
      window.AndroidBridge?.getFCMToken?.(),
      window.Android?.getFCMToken?.(),
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.trim().length > 0) return c.trim();
    }
  } catch { /* ignore */ }
  return null;
}

async function saveTokenToProfile(token: string): Promise<boolean> {
  const trimmed = (token || "").trim();
  if (!trimmed) return false;

  const { data: sessionData } = await supabase.auth.getSession();
  let user = sessionData.session?.user ?? null;
  if (!user) {
    const { data: userData } = await supabase.auth.getUser();
    user = userData.user ?? null;
  }
  if (!user) {
    rememberStatus("waiting_for_login_session");
    return false;
  }

  const marker = `${user.id}:${trimmed}`;
  try {
    if (typeof window !== "undefined" && window.localStorage.getItem(SYNCED_KEY) === marker) {
      rememberStatus("refreshing_saved_token");
    }
  } catch { /* ignore */ }

  const { error } = await supabase
    .from("profiles")
    .update({ fcm_token: trimmed })
    .eq("id", user.id);

  if (error) {
    rememberStatus(`save_failed:${error.message}`);
    console.warn("[useFcmToken] gagal menyimpan fcm_token:", error.message);
    return false;
  }

  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LS_KEY, trimmed);
      window.localStorage.setItem(SYNCED_KEY, marker);
    }
  } catch { /* ignore */ }
  rememberStatus("saved");
  return true;
}

async function syncStoredToken(): Promise<boolean> {
  try {
    const injected = readNativeInjectedToken();
    if (injected) {
      try { window.localStorage.setItem(LS_KEY, injected); } catch { /* ignore */ }
      const ok = await saveTokenToProfile(injected);
      if (ok) return true;
    }
    const existing = window.localStorage.getItem(LS_KEY);
    if (existing) return await saveTokenToProfile(existing);
  } catch { /* ignore */ }
  return false;
}

function queueTokenSyncRetries() {
  for (const delay of SYNC_RETRY_DELAYS) {
    window.setTimeout(() => {
      void syncStoredToken();
    }, delay);
  }
}

async function registerNativePushNotifications() {
  if (nativeRegistrationStarted) return;

  try {
    const [{ Capacitor }, { PushNotifications }] = await Promise.all([
      import("@capacitor/core"),
      import("@capacitor/push-notifications"),
    ]);

    if (!Capacitor.isNativePlatform()) return;
    nativeRegistrationStarted = true;
    rememberStatus("native_detected");

    try { await ensureNativeNotificationChannels(); } catch { /* Android only / already exists */ }

    await PushNotifications.addListener("registration", (token) => {
      try {
        window.localStorage.setItem(LS_KEY, token.value);
      } catch { /* ignore */ }
      rememberStatus("token_received");
      void saveTokenToProfile(token.value);
      queueTokenSyncRetries();
    });

    await PushNotifications.addListener("registrationError", (error) => {
      nativeRegistrationStarted = false;
      rememberStatus(`registration_error:${error.error}`);
      console.warn("[useFcmToken] registrasi push notification gagal:", error.error);
    });

    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      rememberStatus("push_action_opened");
      queueNotificationOpen(action.notification?.data as NotificationData);
    });

    if (!foregroundListenerStarted) {
      foregroundListenerStarted = true;
      try {
        const { LocalNotifications } = await import("@capacitor/local-notifications");
        await LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
          rememberStatus("local_action_opened");
          queueNotificationOpen(action.notification?.extra as NotificationData);
        });
      } catch { /* LocalNotifications may be unavailable in web */ }
      await PushNotifications.addListener("pushNotificationReceived", async (notification) => {
        rememberStatus("push_received_foreground");
        // Panggilan masuk (data-only, type=call) sudah ditampilkan sebagai
        // notifikasi call native oleh Android (ID 2026) dan ditangani
        // CallCenter. Jangan menjadwalkan LocalNotification tambahan —
        // itu memicu notifikasi ganda dan navigasi tak diinginkan ke /chat.
        if (isCallData(notification.data as NotificationData)) {
          rememberStatus("push_received_call_skipped");
          return;
        }
        try {
          const { LocalNotifications } = await import("@capacitor/local-notifications");
          await ensureNativeNotificationChannels();
          const localPermission = await LocalNotifications.requestPermissions();
          if (localPermission.display !== "granted") {
            rememberStatus(`local_permission_${localPermission.display}`);
            return;
          }
          await LocalNotifications.schedule({
            notifications: [{
              id: Math.floor(Date.now() % 2147483647),
              title: getNotificationTitle(notification),
              body: getNotificationBody(notification),
              channelId: getNotificationChannelId(notification.data as NotificationData),
              extra: notification.data,
            }],
          });
          rememberStatus("foreground_local_shown");
        } catch (error) {
          rememberStatus(`foreground_local_error:${error instanceof Error ? error.message : String(error)}`);
        }
      });
    }

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== "granted") {
      nativeRegistrationStarted = false;
      rememberStatus(`permission_${permission.receive}`);
      return;
    }

    // Minta token FCM dari sisi native. Diberi flag agar tidak spam dialog/registrasi
    // setiap render, tapi tetap aman dipanggil ulang setelah reload aplikasi.
    if (!window.localStorage.getItem(REGISTRATION_FLAG_KEY)) {
      window.localStorage.setItem(REGISTRATION_FLAG_KEY, "1");
    }
    await PushNotifications.register();
    rememberStatus("registration_requested");
  } catch (error) {
    nativeRegistrationStarted = false;
    rememberStatus(`plugin_error:${error instanceof Error ? error.message : String(error)}`);
    console.warn("[useFcmToken] plugin push notification native belum tersedia:", error);
  }
}

/**
 * Membaca token FCM yang disuntikkan oleh wrapper Android/iOS
 * (Capacitor / WebView) lalu menyimpannya ke `profiles.fcm_token`.
 *
 * Sumber token:
 *   1. `localStorage.getItem('fcm_token')` — dibaca saat hook mount.
 *   2. `window.onFCMTokenReceived(token)` — dipanggil oleh native side
 *      ketika token baru diterima / di-refresh oleh FCM.
 *   3. Event `storage` — jika tab lain memperbarui LocalStorage.
 */
export function useFcmToken() {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const openUrl = (url: string, replace = false) => {
      if (url.startsWith("/chat?")) {
        const params = new URLSearchParams(url.split("?")[1] ?? "");
        navigate({ to: "/chat", search: { room: params.get("room") ?? undefined }, replace });
      } else if (url === "/chat") {
        navigate({ to: "/chat", replace });
      } else if (url.startsWith("/")) {
        if (replace) window.location.replace(url);
        else window.location.assign(url);
      }
    };

    const pendingUrl = window.sessionStorage.getItem("notification:pendingUrl");
    if (pendingUrl) {
      window.sessionStorage.removeItem("notification:pendingUrl");
      openUrl(pendingUrl, true);
    }

    const onNotificationOpen = (event: Event) => {
      const url = (event as CustomEvent<{ url?: string }>).detail?.url;
      if (url) openUrl(url);
    };
    window.addEventListener("notification-open-url", onNotificationOpen as EventListener);

    // (1) Token yang sudah ada di LocalStorage saat halaman dimuat.
    void syncStoredToken();

    // (1b) Jika berjalan di aplikasi native Capacitor, daftar langsung ke
    // plugin PushNotifications agar token FCM tidak bergantung pada injection
    // manual dari MainActivity/WebView wrapper.
    void registerNativePushNotifications();

    // (2) Callback global untuk native side. Wrapper Android bisa memanggil
    // salah satu nama berikut: onFCMTokenReceived / injectFCMToken / setFCMToken.
    const acceptToken = (token: unknown) => {
      const t = typeof token === "string" ? token.trim() : "";
      if (!t) return;
      try { window.localStorage.setItem(LS_KEY, t); } catch { /* ignore */ }
      void saveTokenToProfile(t);
      queueTokenSyncRetries();
    };
    const previous = window.onFCMTokenReceived;
    const previousInject = window.injectFCMToken;
    const previousSet = window.setFCMToken;
    window.onFCMTokenReceived = (token: string) => {
      acceptToken(token);
      if (typeof previous === "function") {
        try { previous(token); } catch { /* ignore */ }
      }
    };
    window.injectFCMToken = (token: string) => {
      acceptToken(token);
      if (typeof previousInject === "function") {
        try { previousInject(token); } catch { /* ignore */ }
      }
    };
    window.setFCMToken = (token: string) => {
      acceptToken(token);
      if (typeof previousSet === "function") {
        try { previousSet(token); } catch { /* ignore */ }
      }
    };

    // (2b) Polling ringan untuk membaca token yang di-set wrapper ke
    // window.FCMToken / bridge Android sebelum hook sempat mount.
    const pollTimers: number[] = [];
    for (const delay of [200, 800, 2000, 5000, 12000]) {
      pollTimers.push(window.setTimeout(() => { void syncStoredToken(); }, delay));
    }

    // (3) Sinkron antar tab.
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY && e.newValue) void saveTokenToProfile(e.newValue);
    };
    window.addEventListener("storage", onStorage);

    const retryNativeRegistration = () => {
      void syncStoredToken();
      void registerNativePushNotifications();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") retryNativeRegistration();
    };
    window.addEventListener("focus", retryNativeRegistration);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // (4) Coba lagi setelah user selesai login (SIGNED_IN).
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "INITIAL_SESSION" || event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        void syncStoredToken();
        queueTokenSyncRetries();
        void registerNativePushNotifications();
      }
      if (event === "SIGNED_OUT") {
        try { window.localStorage.removeItem(SYNCED_KEY); } catch { /* ignore */ }
      }
    });

    return () => {
      window.removeEventListener("notification-open-url", onNotificationOpen as EventListener);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", retryNativeRegistration);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      for (const t of pollTimers) window.clearTimeout(t);
      if (window.onFCMTokenReceived) window.onFCMTokenReceived = previous;
      if (window.injectFCMToken) window.injectFCMToken = previousInject;
      if (window.setFCMToken) window.setFCMToken = previousSet;
      sub.subscription.unsubscribe();
    };
  }, [navigate]);
}
